import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { APP, firstPermittedApp, ROUTES } from '../../config';
import { useAuth } from '../../lib/auth';
import {
  PROFILE_REDIRECT_PATH,
  PROFILE_REDIRECT_SECONDS,
  profileRedirectMessage,
  shouldRedirectToProfile,
} from './accessDeniedRedirect';

/**
 * Shared full-page error / access-denied screen.
 *
 * The same component handles every "we can't show you that" scenario in
 * the app, so the user always sees a single, consistent, branded page
 * instead of a mix of raw 404s, blank screens or scary red ⚠ toasts.
 *
 * The four supported reasons (and their EXACT copy — locked by the
 * product brief):
 *
 *   ┌──────────────────────┬──────────────────────────────────────────────────────────────────────────────┐
 *   │ reason               │ message                                                                      │
 *   ├──────────────────────┼──────────────────────────────────────────────────────────────────────────────┤
 *   │ unauthorized         │ "You don't have permission to access this module. Please contact your admin." │
 *   │ not-for-superadmin   │ "This app is not available to super admins."                                 │
 *   │ not-for-admin        │ "This app is not available to admins."                                       │
 *   │ not-found            │ "The page you are looking for does not exist."                               │
 *   └──────────────────────┴──────────────────────────────────────────────────────────────────────────────┘
 *
 * The component can be used in two ways:
 *
 *   1. Programmatically by a guard:
 *
 *        return <AccessDenied reason="not-for-superadmin" />;
 *
 *   2. As a real route — ``/access-denied?reason=unauthorized``.  This
 *      is what the axios 403 handler navigates to so the user gets a
 *      page (with browser history) rather than a transient toast.
 *      When ``reason`` is missing or unknown we fall back to the
 *      generic ``"unauthorized"`` message — the safest default.
 *
 * When the reason is a module-access denial (everything except
 * ``not-found``) we send the user to their profile page (``/me``)
 * after ``PROFILE_REDIRECT_SECONDS``.  Profile is not permission-gated,
 * so it is a safe landing even when they have zero permitted apps.
 *
 * The "Return to home" button is dynamic: it uses ``firstPermittedApp``
 * so super-admins go to ``/super-admin`` and org users go to whatever
 * their first permitted business app is.  If the user has zero
 * permitted apps we still offer "Go to profile" as the way out.
 */

// Locked by product brief — DO NOT paraphrase.
const MESSAGES = {
  'unauthorized':
    "You don't have permission to access this module. Please contact your admin.",
  'not-for-superadmin':
    'This app is not available to super admins.',
  'not-for-admin':
    'This app is not available to admins.',
  'not-found':
    'The page you are looking for does not exist.',
};

const TITLES = {
  'unauthorized':       'Access denied',
  'not-for-superadmin': 'App not available',
  'not-for-admin':      'App not available',
  'not-found':          'Page not found',
};

const ICONS = {
  'unauthorized':       '🔒',
  'not-for-superadmin': '🛡️',
  'not-for-admin':      '🛡️',
  'not-found':          '🧭',
};

export default function AccessDenied({ reason: reasonProp }) {
  // Allow the page to be opened as a real route — read ``?reason=...``
  // when no prop was supplied.  This is what the axios 403 handler
  // uses (it can't pass props through ``window.location.replace``).
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const reasonRaw = (reasonProp || searchParams.get('reason') || '').trim();

  // Anything we don't recognise falls back to the generic
  // "unauthorized" message — the safest default per the brief.
  const reason = MESSAGES[reasonRaw] ? reasonRaw : 'unauthorized';

  const message = MESSAGES[reason];
  const title   = TITLES[reason];
  const icon    = ICONS[reason];

  const { user, logout } = useAuth();
  const home = firstPermittedApp(user);
  const redirectToProfile = shouldRedirectToProfile(reason);
  const [secondsLeft, setSecondsLeft] = useState(PROFILE_REDIRECT_SECONDS);

  useEffect(() => {
    if (!redirectToProfile) return undefined;

    setSecondsLeft(PROFILE_REDIRECT_SECONDS);

    const timeoutId = window.setTimeout(() => {
      navigate(PROFILE_REDIRECT_PATH, { replace: true });
    }, PROFILE_REDIRECT_SECONDS * 1000);

    const intervalId = window.setInterval(() => {
      setSecondsLeft((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => {
      window.clearTimeout(timeoutId);
      window.clearInterval(intervalId);
    };
  }, [redirectToProfile, navigate]);

  return (
    <div className="ty-fullscreen-center" role="alert" aria-live="polite">
      <div style={{ maxWidth: 540, padding: 24, textAlign: 'center' }}>
        <div
          aria-hidden
          style={{
            width: 72,
            height: 72,
            margin: '0 auto 18px',
            borderRadius: 22,
            display: 'grid',
            placeItems: 'center',
            color: '#fff',
            fontSize: 32,
            background: reason === 'not-found'
              ? 'linear-gradient(135deg, #7980ff 0%, #656cfd 100%)'
              : 'linear-gradient(135deg, #f9771d 0%, #ec4899 100%)',
            boxShadow: '0 14px 30px rgba(236, 72, 153, 0.30)',
          }}
        >
          {icon}
        </div>

        <h1 style={{ margin: '0 0 8px' }}>{title}</h1>

        {/* Exact, brief-locked copy — see MESSAGES table above. */}
        <p className="ty-muted" style={{ marginTop: 0, marginBottom: redirectToProfile ? 12 : 20 }}>
          {message}
        </p>

        {redirectToProfile && (
          <p
            className="ty-muted"
            role="status"
            style={{ marginTop: 0, marginBottom: 20 }}
          >
            {profileRedirectMessage(secondsLeft)}
          </p>
        )}

        <div className="ty-row" style={{ justifyContent: 'center', gap: 8 }}>
          {redirectToProfile && (
            <Link className="ty-btn" to={PROFILE_REDIRECT_PATH} replace>
              Go to profile
            </Link>
          )}

          {home ? (
            <Link className={redirectToProfile ? 'ty-btn ty-btn--ghost' : 'ty-btn'} to={home.home}>
              Return to home
            </Link>
          ) : (
            !redirectToProfile && (
              <Link className="ty-btn" to={ROUTES.LOGIN}>
                Back to sign in
              </Link>
            )
          )}

          {/* "Go back" — uses the browser stack so users opened on a
              deep link still have a way out.  Falls back to the
              landing page if there's no history. */}
          <button
            type="button"
            className="ty-btn ty-btn--ghost"
            onClick={() => {
              if (window.history.length > 1) window.history.back();
              else if (home) window.location.assign(home.home);
              else if (redirectToProfile) navigate(PROFILE_REDIRECT_PATH, { replace: true });
            }}
          >
            Go back
          </button>

          {user && (
            <button
              type="button"
              className="ty-btn ty-btn--ghost"
              onClick={logout}
            >
              Sign out
            </button>
          )}
        </div>

        <p
          className="ty-muted"
          style={{ marginTop: 24, fontSize: 12, opacity: 0.6 }}
        >
          {APP.name}
        </p>
      </div>
    </div>
  );
}
